import { runtimeConfig } from "@/runtime-config";
import { EMBEDDED_PROTOCOL_VERSION } from "./protocol";

/**
 * 嵌入端运行时配置（设计 §15.1）。
 *
 * `TICKBIRD_EMBED_PARENT_ORIGINS` 是"谁能嵌入 Tickbird"的**唯一配置源**：
 * 同一个值在启动时一份渲染成前端白名单，一份派生成 CSP `frame-ancestors`。
 * 留空允许任意宿主；填写后只允许列表中的有效 Origin。
 */

function toOriginList(value: string | string[] | undefined): readonly string[] {
  const candidates = Array.isArray(value) ? value : (value ?? "").split(",");
  const origins: string[] = [];

  for (const candidate of candidates) {
    const trimmed = candidate.trim();
    if (!trimmed) {
      continue;
    }
    try {
      // 只接受完整 Origin（scheme + host + 可选 port）。带路径、带尾斜杠的值一律丢弃。
      if (new URL(trimmed).origin === trimmed && !origins.includes(trimmed)) {
        origins.push(trimmed);
      } else if (new URL(trimmed).origin !== trimmed) {
        console.warn("忽略非法的宿主 Origin（必须是 scheme + host + 可选 port）：", trimmed);
      }
    } catch {
      console.warn("忽略无法解析的宿主 Origin：", trimmed);
    }
  }

  return Object.freeze(origins);
}

function toProtocolVersion(value: number | string | undefined): typeof EMBEDDED_PROTOCOL_VERSION {
  // 版本描述代码实际实现的契约，部署配置不能把 v2 实现伪装成旧版。
  if (value !== undefined && String(value).trim() && Number(value) !== EMBEDDED_PROTOCOL_VERSION) {
    console.warn(`忽略不支持的嵌入协议配置 ${value}：当前实现仅支持 v${EMBEDDED_PROTOCOL_VERSION}，请配套升级宿主与 iframe。`);
  }
  return EMBEDDED_PROTOCOL_VERSION;
}

function toStandaloneUrl(value: string | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed) {
    return null;
  }
  try {
    return new URL(trimmed).toString().replace(/\/$/, "");
  } catch {
    console.warn("忽略无法解析的独立工作台地址：", trimmed);
    return null;
  }
}

export const embeddedConfig = {
  /** 留空时允许任意宿主嵌入并建立父子通道。 */
  allowAnyParentOrigin: Array.isArray(runtimeConfig.embeddedParentOrigins)
    ? runtimeConfig.embeddedParentOrigins.every((origin) => !origin.trim())
    : !runtimeConfig.embeddedParentOrigins?.trim(),
  /** 填写后允许嵌入并建立父子通道的宿主 Origin。 */
  parentOrigins: toOriginList(runtimeConfig.embeddedParentOrigins),
  /** 两端不匹配时拒绝建链。 */
  protocolVersion: toProtocolVersion(runtimeConfig.embeddedProtocolVersion),
  /** "在完整工作台打开"的目标地址；未配置时隐藏该入口。 */
  standaloneUrl: toStandaloneUrl(runtimeConfig.embeddedStandaloneUrl),
} as const;

export function isAllowedParentOrigin(origin: string): boolean {
  return embeddedConfig.allowAnyParentOrigin || embeddedConfig.parentOrigins.includes(origin);
}
